import { Base } from '@studiometa/js-toolkit';

/**
 * WishlistCardAnimation component - handles card cycling animation on mobile.
 */
export default class WishlistCardAnimation extends Base {
  static config = {
    name: 'WishlistCardAnimation',
    refs: ['wishlistCard[]', 'clickIndicator'],
  };

  // -------------------------------------------------------------------------
  // Constants
  // -------------------------------------------------------------------------

  /** @type {number} Mobile breakpoint width */
  static MOBILE_BREAKPOINT = 1024;

  /** @type {number} Initial card index shown on top */
  static INITIAL_CARD_INDEX = 2;

  /** @type {number[]} Rotations for each card */
  static CARD_ROTATIONS = [-5, 4, -7];

  /** @type {{ x: number, y: number }[]} Initial translate values for each card */
  static INITIAL_TRANSLATES = [
    { x: 0, y: 0 },
    { x: 12, y: 12 },
    { x: 16, y: 24 },
  ];

  /** @type {number} Animation duration in milliseconds */
  static ANIMATION_DURATION = 300;

  /** @type {number} Lift animation duration in milliseconds */
  static LIFT_DURATION = 150;

  /** @type {number} Lift offset in pixels */
  static LIFT_OFFSET = 10;

  /** @type {number} Scale factor when card is on top */
  static TOP_CARD_SCALE = 1.05;

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  /** @type {boolean} Whether the viewport is mobile */
  isMobile = window.innerWidth < WishlistCardAnimation.MOBILE_BREAKPOINT;

  /** @type {number} Current card index shown on top */
  currentCardIndex = WishlistCardAnimation.INITIAL_CARD_INDEX;

  /** @type {Array<{element: HTMLElement, container: HTMLElement, index: number, initialTranslate: {x: number, y: number}, rotate: number}>} Card elements data */
  cardElements = [];

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Component mounted.
   */
  mounted() {
    if (this.isMobile) {
      setTimeout(() => {
        this.setupMobileCards();
      }, 100);
    }
  }

  /**
   * Handle resize events.
   * @param {{ width: number }} props Resize service props.
   */
  resized({ width }) {
    const wasMobile = this.isMobile;
    this.isMobile = width < WishlistCardAnimation.MOBILE_BREAKPOINT;

    if (!wasMobile && this.isMobile) {
      setTimeout(() => {
        this.setupMobileCards();
      }, 100);
    } else if (wasMobile && !this.isMobile) {
      this.cleanupMobileCards();
    }
  }

  // -------------------------------------------------------------------------
  // Methods
  // -------------------------------------------------------------------------

  /**
   * Setup mobile cards with initial positions and click handlers.
   */
  setupMobileCards() {
    const cardContainers = /** @type {HTMLElement[]} */ (this.$refs.wishlistCard);

    if (!cardContainers || cardContainers.length === 0) {
      return;
    }

    this.cardElements = [];

    cardContainers.forEach((container, index) => {
      const cardElement = /** @type {HTMLElement | null} */ (container.querySelector('.card'));
      if (!cardElement) {
        return;
      }

      const rotate = WishlistCardAnimation.CARD_ROTATIONS[index];
      const initialTranslate =
        WishlistCardAnimation.INITIAL_TRANSLATES[index] ||
        WishlistCardAnimation.INITIAL_TRANSLATES[0];

      this.applyCardStyles(cardElement, container, index, initialTranslate, rotate);

      this.cardElements.push({
        element: cardElement,
        container,
        index,
        initialTranslate,
        rotate,
      });
    });

    this.attachClickHandlers();
  }

  /**
   * Apply initial styles to a card element.
   * @param {HTMLElement} cardElement Card element
   * @param {HTMLElement} container Card container
   * @param {number} index Card index
   * @param {{ x: number, y: number }} initialTranslate Initial translate values
   * @param {number} rotate Rotation value
   */
  applyCardStyles(cardElement, container, index, initialTranslate, rotate) {
    const transform = `translate(${initialTranslate.x}px, ${initialTranslate.y}px) rotate(${rotate}deg)`;

    cardElement.style.setProperty('transform', transform, 'important');
    cardElement.style.zIndex = String(index + 1);
    cardElement.style.cursor = 'pointer';
    cardElement.style.setProperty(
      'transition',
      `transform ${WishlistCardAnimation.ANIMATION_DURATION}ms ease, z-index 0s`,
      'important',
    );
    cardElement.style.setProperty('pointer-events', 'auto', 'important');
    cardElement.classList.remove('pointer-events-none');

    container.style.pointerEvents = 'auto';
    container.style.cursor = 'pointer';
    container.style.setProperty('z-index', String(index + 1), 'important');
  }

  /**
   * Attach click handlers to cards.
   */
  attachClickHandlers() {
    this.cardElements.forEach(({ element }) => {
      const clickHandler = (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        this.cycleCards();
        return false;
      };

      element.addEventListener('click', clickHandler, true);
    });
  }

  /**
   * Get initial translate values based on card index.
   * @param {number} index Card index
   * @returns {{ x: number, y: number }} Translate values in pixels
   */
  getInitialTranslate(index) {
    return (
      WishlistCardAnimation.INITIAL_TRANSLATES[index] || WishlistCardAnimation.INITIAL_TRANSLATES[0]
    );
  }

  /**
   * Cycle to show the next card on top.
   */
  cycleCards() {
    if (!this.cardElements || this.cardElements.length === 0) {
      return;
    }

    const indicator = /** @type {HTMLElement} */ (this.$refs.clickIndicator);
    if (indicator) {
      indicator.style.opacity = '0';
      indicator.style.transition = `opacity ${WishlistCardAnimation.ANIMATION_DURATION}ms ease`;
    }

    this.currentCardIndex = (this.currentCardIndex + 1) % this.cardElements.length;

    this.cardElements.forEach(({ element, container, index, initialTranslate, rotate }) => {
      const distance =
        (index - this.currentCardIndex + this.cardElements.length) % this.cardElements.length;
      const zIndex = this.cardElements.length - distance;

      element.style.zIndex = String(zIndex);
      if (container) {
        container.style.setProperty('z-index', String(zIndex), 'important');
      }

      if (index === this.currentCardIndex) {
        this.animateCardToTop(element, initialTranslate, rotate);
      } else {
        this.resetCardPosition(element, initialTranslate, rotate);
      }
    });
  }

  /**
   * Animate card to top position with lift effect.
   * @param {HTMLElement} element Card element
   * @param {{ x: number, y: number }} initialTranslate Initial translate values
   * @param {number} rotate Rotation value
   */
  animateCardToTop(element, initialTranslate, rotate) {
    const liftY = initialTranslate.y - WishlistCardAnimation.LIFT_OFFSET;
    const liftTransform = `translate(${initialTranslate.x}px, ${liftY}px) rotate(${rotate}deg) scale(${WishlistCardAnimation.TOP_CARD_SCALE})`;

    element.style.setProperty('transform', liftTransform, 'important');

    setTimeout(() => {
      const finalTransform = `translate(${initialTranslate.x}px, ${initialTranslate.y}px) rotate(${rotate}deg)`;
      element.style.setProperty('transform', finalTransform, 'important');
    }, WishlistCardAnimation.LIFT_DURATION);
  }

  /**
   * Reset card to initial position.
   * @param {HTMLElement} element Card element
   * @param {{ x: number, y: number }} initialTranslate Initial translate values
   * @param {number} rotate Rotation value
   */
  resetCardPosition(element, initialTranslate, rotate) {
    const transform = `translate(${initialTranslate.x}px, ${initialTranslate.y}px) rotate(${rotate}deg)`;
    element.style.setProperty('transform', transform, 'important');
  }

  /**
   * Cleanup mobile cards styles when switching to desktop.
   */
  cleanupMobileCards() {
    const cardContainers = /** @type {HTMLElement[]} */ (this.$refs.wishlistCard);

    cardContainers?.forEach((container) => {
      const cardElement = /** @type {HTMLElement | null} */ (container.querySelector('.card'));
      if (cardElement) {
        cardElement.style.transform = '';
        cardElement.style.zIndex = '';
        cardElement.style.cursor = '';
        cardElement.style.transition = '';
        cardElement.classList.add('pointer-events-none');
      }
      container.style.cursor = '';
      container.style.zIndex = '';
    });

    this.cardElements = [];
    this.currentCardIndex = WishlistCardAnimation.INITIAL_CARD_INDEX;
  }
}
